const importMetaApiUrl = (import.meta?.env?.VITE_API_URL ?? '').trim();
const processApiUrl = typeof process !== 'undefined' && process.env ? (process.env.VITE_API_URL || '').trim() : '';
const envApiUrl = importMetaApiUrl || processApiUrl;
const apiDomain = envApiUrl.replace(/\/+$/, '');
const API_PATH_PREFIX = '/api';
export const API_BASE = apiDomain ? `${apiDomain}${API_PATH_PREFIX}` : API_PATH_PREFIX;

/**
 * Build an absolute API URL for a given endpoint.
 * @param {string} endpoint
 * @returns {string}
 */
export function buildApiUrl(endpoint = '') {
  const suffix = endpoint.startsWith('/') ? endpoint : `/${endpoint}`;
  const path = suffix.startsWith(API_PATH_PREFIX) ? suffix : `${API_PATH_PREFIX}${suffix}`;
  return apiDomain ? `${apiDomain}${path}` : path;
}

/**
 * Build an absolute URL for an endpoint served at the *root* of the API host.
 *
 * `buildApiUrl` forces the `/api` prefix, which is right for every REST route
 * but wrong for the OAuth surface: the authorization server is mounted at the
 * host root (`/oauth/*`, `/oauth2/*`, `/.well-known/*`) because those paths are
 * baked into Hydra's configuration and into what clients discover. Routing them
 * through `/api` would simply 404.
 *
 * @param {string} path Root-relative path, e.g. '/oauth/consent'
 * @returns {string}
 */
export function buildRootUrl(path = '') {
  const suffix = path.startsWith('/') ? path : `/${path}`;
  return apiDomain ? `${apiDomain}${suffix}` : suffix;
}

/**
 * Absolute base of the API host — the very thing `buildRootUrl` builds on.
 *
 * Path-bearing on purpose. Anything that has to *name* a root-mounted endpoint
 * outside a fetch (the `returnTo` that resumes a consent handshake, and the
 * check that decides whether such a URL may be followed) must land on the same
 * URL a real request would reach. Deriving those from a path-stripped origin
 * instead would silently disagree with every request the moment `VITE_API_URL`
 * carries a path prefix, and point at a path that does not exist.
 *
 * Falls back to the page origin when the SPA is served from the API host, which
 * is also when `buildRootUrl` returns a same-origin relative path — so the two
 * still describe the same URL.
 *
 * @type {string}
 */
export const API_ROOT = (() => {
  const fallback = typeof window !== 'undefined' && window.location ? window.location.origin : '';
  if (!apiDomain) return fallback;
  try {
    return new URL(apiDomain, fallback || undefined).href.replace(/\/+$/, '');
  } catch (parseError) {
    return fallback;
  }
})();

/**
 * Carry the machine-readable parts of an error response onto the thrown Error.
 *
 * `throw new Error(errorData.error)` keeps only the sentence. The signature
 * routes refuse an unverified document with `{error, signatureState,
 * requiresAttestation: true}` — a refusal the UI has to *recognise* in order to
 * ask for the attestation, not merely display. Losing those two fields is the
 * difference between "the proposal workflow is stuck" and "the operator is
 * asked to attest what they checked".
 *
 * @param {Error} error The error about to be thrown
 * @param {Object} body Parsed error body from the server
 * @param {number} status HTTP status
 * @returns {Error} The same error, decorated
 */
function decorateApiError(error, body, status) {
  error.status = status;
  if (body && typeof body === 'object') {
    if (body.signatureState !== undefined) error.signatureState = body.signatureState;
    if (body.requiresAttestation !== undefined) error.requiresAttestation = body.requiresAttestation;
    if (body.signatureInfo !== undefined) error.signatureInfo = body.signatureInfo;
  }
  return error;
}

/**
 * Build the attestation fields a signature route accepts on its request body.
 *
 * Returns an empty object when nothing was attested, so a `bound` document is
 * still acted on with no attestation at all — the flag must never default to
 * true, on any path.
 *
 * @param {{attestUnverifiedSignature?: boolean, attestationNotes?: string|null}|null} [attestation]
 * @returns {{attestUnverifiedSignature?: boolean, attestationNotes?: string}}
 */
export function signatureAttestationBody(attestation = null) {
  if (!attestation || attestation.attestUnverifiedSignature !== true) return {};
  const body = { attestUnverifiedSignature: true };
  const notes = typeof attestation.attestationNotes === 'string' ? attestation.attestationNotes.trim() : '';
  if (notes) body.attestationNotes = notes;
  return body;
}

/**
 * Generic API call function with authentication
 */
async function apiCall(endpoint, options = {}) {
  const url = buildApiUrl(endpoint);
  const config = {
    headers: {
      'Content-Type': 'application/json',
      ...options.headers,
    },
    credentials: 'include',
    ...options,
  };

  if (options.body && typeof options.body === 'object') {
    config.body = JSON.stringify(options.body);
  }

  const response = await fetch(url, config);

  const rawText = await response.text();

  if (!response.ok) {
    let errorData;
    try {
      errorData = JSON.parse(rawText);
    } catch (parseError) {
      errorData = { error: rawText || 'Request failed' };
    }
    throw decorateApiError(new Error(errorData.error || `HTTP ${response.status}`), errorData, response.status);
  }

  if (!rawText) {
    return {};
  }

  try {
    return JSON.parse(rawText);
  } catch (parseError) {
    console.warn('Received non-JSON response from API:', { url, status: response.status });
    return {};
  }
}

/**
 * Upload file via multipart form data
 * @param {string} endpoint - API endpoint
 * @param {FormData} formData - Form data with file
 * @returns {Promise<Object>} Response data
 */
async function uploadFile(endpoint, formData) {
  const url = buildApiUrl(endpoint);
  const response = await fetch(url, {
    method: 'POST',
    credentials: 'include',
    body: formData,
    // Don't set Content-Type header - browser will set it with boundary
  });

  const rawText = await response.text();

  if (!response.ok) {
    let errorData;
    try {
      errorData = JSON.parse(rawText);
    } catch (parseError) {
      errorData = { error: rawText || 'Upload failed' };
    }
    // Include signatureInfo / signatureState / requiresAttestation if present
    // in the error response, so the caller can react rather than only report.
    throw decorateApiError(new Error(errorData.error || `HTTP ${response.status}`), errorData, response.status);
  }

  try {
    return JSON.parse(rawText);
  } catch (parseError) {
    return {};
  }
}

/**
 * Fetch a blob from an endpoint (for file downloads)
 * @param {string} endpoint - API endpoint
 * @returns {Promise<Blob>} Response blob
 */
async function fetchBlob(endpoint) {
  const url = buildApiUrl(endpoint);
  const response = await fetch(url, {
    credentials: 'include',
  });

  if (!response.ok) {
    let errorData;
    try {
      const rawText = await response.text();
      errorData = JSON.parse(rawText);
    } catch (parseError) {
      errorData = { error: 'Download failed' };
    }
    throw new Error(errorData.error || `HTTP ${response.status}`);
  }

  return response.blob();
}

/**
 * Customers API
 */
export const customersApi = {
  /**
   * Get paginated customers list
   */
  async getAll(params = {}) {
    const searchParams = new URLSearchParams(params);
    return apiCall(`/customers?${searchParams}`);
  },

  /**
   * Get customer by ID
   */
  async getById(id) {
    return apiCall(`/customers/${encodeURIComponent(id)}`);
  },

  /**
   * Create new customer
   */
  async create(customer) {
    return apiCall('/customers', {
      method: 'POST',
      body: customer,
    });
  },

  /**
   * Update customer
   */
  async update(id, customer) {
    return apiCall(`/customers/${encodeURIComponent(id)}`, {
      method: 'PUT',
      body: customer,
    });
  },

  /**
   * Delete customer
   */
  async delete(id) {
    return apiCall(`/customers/${encodeURIComponent(id)}`, {
      method: 'DELETE',
    });
  },

  /**
   * Get user info for a customer
   */
  async getUserInfo(id) {
    return apiCall(`/customers/${encodeURIComponent(id)}/user`);
  },

  /**
   * Create user account for a customer
   * @param {string} id - Customer ID
   * @param {string} password - Password for the new user
   */
  async createUser(id, password) {
    return apiCall(`/customers/${encodeURIComponent(id)}/create-user`, {
      method: 'POST',
      body: { password },
    });
  },
};

/**
 * Products API
 */
export const productsApi = {
  /**
   * Get all products with optional filters
   * @param {Object} params - Query parameters (search, category, active)
   */
  async getAll(params = {}) {
    const searchParams = new URLSearchParams(params);
    return apiCall(`/products?${searchParams}`);
  },

  /**
   * Get product by ID with all variants
   */
  async getById(id) {
    return apiCall(`/products/${encodeURIComponent(id)}`);
  },

  /**
   * Create new product
   * @param {Object} product - { code, name, description, category, isActive }
   */
  async create(product) {
    return apiCall('/products', {
      method: 'POST',
      body: product,
    });
  },

  /**
   * Update product
   */
  async update(id, product) {
    return apiCall(`/products/${encodeURIComponent(id)}`, {
      method: 'PUT',
      body: product,
    });
  },

  /**
   * Delete product (cascades to variants)
   */
  async delete(id) {
    return apiCall(`/products/${encodeURIComponent(id)}`, {
      method: 'DELETE',
    });
  },

  /**
   * Get variants for a product
   */
  async getVariants(productId) {
    return apiCall(`/products/${encodeURIComponent(productId)}/variants`);
  },

  /**
   * Create variant for a product
   * @param {number} productId - Product ID
   * @param {Object} variant - { code, name, description, price, unit, billingCycle, isActive }
   */
  async createVariant(productId, variant) {
    return apiCall(`/products/${encodeURIComponent(productId)}/variants`, {
      method: 'POST',
      body: variant,
    });
  },

  /**
   * Update variant
   */
  async updateVariant(productId, variantId, variant) {
    return apiCall(`/products/${encodeURIComponent(productId)}/variants/${encodeURIComponent(variantId)}`, {
      method: 'PUT',
      body: variant,
    });
  },

  /**
   * Delete variant
   */
  async deleteVariant(productId, variantId) {
    return apiCall(`/products/${encodeURIComponent(productId)}/variants/${encodeURIComponent(variantId)}`, {
      method: 'DELETE',
    });
  },

  /**
   * Get activity types linked to a variant
   */
  async getVariantActivityTypes(productId, variantId) {
    return apiCall(`/products/${encodeURIComponent(productId)}/variants/${encodeURIComponent(variantId)}/activity-types`);
  },

  /**
   * Set activity types for a variant (replaces existing)
   * @param {number} productId - Product ID
   * @param {number} variantId - Variant ID
   * @param {string[]} activityTypes - Array of activity types
   */
  async setVariantActivityTypes(productId, variantId, activityTypes) {
    return apiCall(`/products/${encodeURIComponent(productId)}/variants/${encodeURIComponent(variantId)}/activity-types`, {
      method: 'PUT',
      body: { activityTypes },
    });
  },

  /**
   * Lookup hourly rate by activity type
   * @param {string} activityType - Activity type
   * @param {string} [customerId] - Optional customer ID for custom pricing
   * @param {{date?: string, urgent?: boolean, night?: boolean}} [context] - Optional calendar context for surcharge pricing
   */
  async rateLookup(activityType, customerId = null, context = {}) {
    const params = new URLSearchParams({ activityType });
    if (customerId) params.append('customerId', customerId);
    if (context.date) params.append('date', context.date);
    if (context.urgent) params.append('urgent', 'true');
    if (context.night) params.append('night', 'true');
    return apiCall(`/products/rate-lookup?${params}`);
  },

  /**
   * Get available activity types
   */
  async getActivityTypes() {
    return apiCall('/products/activity-types');
  },
};

/**
 * Variants API - Flat list for order selection
 */
export const variantsApi = {
  /**
   * Get all variants with parent product info
   * @param {Object} params - Query parameters (search, category, active)
   */
  async getAll(params = {}) {
    const searchParams = new URLSearchParams(params);
    return apiCall(`/variants?${searchParams}`);
  },

  /**
   * Get variant by ID with parent product info
   */
  async getById(id) {
    return apiCall(`/variants/${encodeURIComponent(id)}`);
  },
};

/**
 * Variant Condition Prices API - hourly surcharges (weekend, holiday, urgent, night)
 */
export const variantConditionPricesApi = {
  /**
   * Get the condition prices of one variant
   * @param {number} variantId
   */
  async getByVariant(variantId) {
    return apiCall(`/variant-condition-prices?variantId=${encodeURIComponent(variantId)}`);
  },

  /**
   * Create a condition price
   * @param {{variantId: number, conditionCode: string, price: number, notes?: string}} data
   */
  async create(data) {
    return apiCall('/variant-condition-prices', { method: 'POST', body: JSON.stringify(data) });
  },

  /**
   * Update a condition price
   * @param {number} id
   * @param {{price?: number, notes?: string, isActive?: boolean}} data
   */
  async update(id, data) {
    return apiCall(`/variant-condition-prices/${encodeURIComponent(id)}`, { method: 'PUT', body: JSON.stringify(data) });
  },

  /**
   * Delete a condition price
   * @param {number} id
   */
  async delete(id) {
    return apiCall(`/variant-condition-prices/${encodeURIComponent(id)}`, { method: 'DELETE' });
  },
};

/**
 * Customer Prices API - Customer-specific variant pricing
 */
export const customerPricesApi = {
  /**
   * Get all customer variant prices
   * @param {Object} params - Query parameters (customerId, variantId, active)
   */
  async getAll(params = {}) {
    const searchParams = new URLSearchParams(params);
    return apiCall(`/customer-prices?${searchParams}`);
  },

  /**
   * Get custom prices for a specific customer
   */
  async getByCustomer(customerId) {
    return apiCall(`/customer-prices/customer/${encodeURIComponent(customerId)}`);
  },

  /**
   * Get custom prices for a specific variant
   */
  async getByVariant(variantId) {
    return apiCall(`/customer-prices/variant/${encodeURIComponent(variantId)}`);
  },

  /**
   * Get customer price by ID
   */
  async getById(id) {
    return apiCall(`/customer-prices/${encodeURIComponent(id)}`);
  },

  /**
   * Create new customer variant price
   * @param {Object} data - { customerId, variantId, customPrice, notes, isActive }
   */
  async create(data) {
    return apiCall('/customer-prices', {
      method: 'POST',
      body: JSON.stringify(data)
    });
  },

  /**
   * Update customer variant price
   * @param {number} id - Customer price ID
   * @param {Object} data - { customPrice, notes, isActive }
   */
  async update(id, data) {
    return apiCall(`/customer-prices/${encodeURIComponent(id)}`, {
      method: 'PUT',
      body: JSON.stringify(data)
    });
  },

  /**
   * Delete customer variant price
   */
  async delete(id) {
    return apiCall(`/customer-prices/${encodeURIComponent(id)}`, {
      method: 'DELETE'
    });
  }
};

/**
 * Proposals API
 */
export const proposalsApi = {
  /**
   * Get paginated proposals list
   * @param {Object} params - Query parameters (status, customerId, search, page, limit)
   */
  async getAll(params = {}) {
    const searchParams = new URLSearchParams(params);
    return apiCall(`/proposals?${searchParams}`);
  },

  /**
   * Get proposal by ID with items
   */
  async getById(id) {
    return apiCall(`/proposals/${encodeURIComponent(id)}`);
  },

  /**
   * Create new proposal
   * @param {Object} data - { title, description?, customerId?, items?, proposedPrice?, discountPercentage?, notes? }
   */
  async create(data) {
    return apiCall('/proposals', {
      method: 'POST',
      body: data,
    });
  },

  /**
   * Update proposal (draft only)
   * @param {number} id - Proposal ID
   * @param {Object} data - { title?, description?, customerId?, proposedPrice?, discountPercentage?, notes?, items? }
   */
  async update(id, data) {
    return apiCall(`/proposals/${encodeURIComponent(id)}`, {
      method: 'PUT',
      body: data,
    });
  },

  /**
   * Delete proposal (admin only)
   */
  async delete(id) {
    return apiCall(`/proposals/${encodeURIComponent(id)}`, {
      method: 'DELETE',
    });
  },

  /**
   * Submit proposal for approval
   */
  async submit(id) {
    return apiCall(`/proposals/${encodeURIComponent(id)}/submit`, {
      method: 'POST',
    });
  },

  /**
   * Approve proposal (admin-office)
   * @param {number} id - Proposal ID
   * @param {string} [notes] - Optional notes
   */
  async approve(id, notes = null) {
    return apiCall(`/proposals/${encodeURIComponent(id)}/approve`, {
      method: 'POST',
      body: notes ? { notes } : {},
    });
  },

  /**
   * Reject proposal (back to draft)
   * @param {number} id - Proposal ID
   * @param {string} notes - Required rejection notes
   */
  async reject(id, notes) {
    return apiCall(`/proposals/${encodeURIComponent(id)}/reject`, {
      method: 'POST',
      body: { notes },
    });
  },

  /**
   * Send proposal via email
   */
  async send(id) {
    return apiCall(`/proposals/${encodeURIComponent(id)}/send`, {
      method: 'POST',
    });
  },

  /**
   * Mark proposal as approved by customer
   * @param {number} id - Proposal ID
   * @param {string} [notes] - Optional notes
   */
  async customerApprove(id, notes = null) {
    return apiCall(`/proposals/${encodeURIComponent(id)}/customer-approve`, {
      method: 'POST',
      body: notes ? { notes } : {},
    });
  },

  /**
   * Confirm proposal to proceed.
   *
   * A final document the backend could not bind to the PDF we sent (`unbound`)
   * is refused with `{requiresAttestation: true}` until an internal user states
   * that they checked it. That statement travels here, and only here — a
   * `bound` document is confirmed with no attestation, exactly as before.
   *
   * @param {number} id - Proposal ID
   * @param {string} [notes] - Optional notes
   * @param {{attestUnverifiedSignature?: boolean, attestationNotes?: string|null}} [attestation]
   *   Explicit attestation for an unverified document. Omitted for a verified one.
   */
  async confirm(id, notes = null, attestation = null) {
    return apiCall(`/proposals/${encodeURIComponent(id)}/confirm`, {
      method: 'POST',
      body: {
        ...(notes ? { notes } : {}),
        ...signatureAttestationBody(attestation),
      },
    });
  },

  /**
   * Mark proposal as completed
   * @param {number} id - Proposal ID
   * @param {string} [notes] - Optional notes
   */
  async complete(id, notes = null) {
    return apiCall(`/proposals/${encodeURIComponent(id)}/complete`, {
      method: 'POST',
      body: notes ? { notes } : {},
    });
  },

  /**
   * Cancel proposal (admin only)
   * @param {number} id - Proposal ID
   * @param {string} notes - Required cancellation notes
   */
  async cancel(id, notes) {
    return apiCall(`/proposals/${encodeURIComponent(id)}/cancel`, {
      method: 'POST',
      body: { notes },
    });
  },

  /**
   * Get proposal status history
   */
  async getHistory(id) {
    return apiCall(`/proposals/${encodeURIComponent(id)}/history`);
  },

  /**
   * Get PDF URL for proposal
   */
  getPdfUrl(id) {
    return buildApiUrl(`/proposals/${encodeURIComponent(id)}/pdf`);
  },

  /**
   * Upload a signed PDF
   * @param {number} id - Proposal ID
   * @param {string} type - 'customer' or 'final'
   * @param {FormData} formData - Form data with PDF file
   */
  async uploadSignedPdf(id, type, formData) {
    return uploadFile(`/proposals/${encodeURIComponent(id)}/upload-signed?type=${type}`, formData);
  },

  /**
   * Get signed PDF as blob
   * @param {number} id - Proposal ID
   * @param {string} type - 'customer' or 'final'
   */
  async getSignedPdfBlob(id, type) {
    return fetchBlob(`/proposals/${encodeURIComponent(id)}/signed-pdf/${type}`);
  },

  /**
   * Get signature info for a proposal
   * @param {number} id - Proposal ID
   */
  async getSignatureInfo(id) {
    return apiCall(`/proposals/${encodeURIComponent(id)}/signature-info`);
  },

  /**
   * Counter-sign the customer-signed proposal with the company's qualified
   * certificate, producing the final `.p7m`. Admin only.
   *
   * Applying our qualified signature asserts that this is the document we sent.
   * When the backend could not establish that on its own, it refuses with
   * `{requiresAttestation: true}` until a named user asserts it instead; that
   * assertion is the second argument. A `bound` document needs none.
   *
   * @param {number} id - Proposal ID
   * @param {{attestUnverifiedSignature?: boolean, attestationNotes?: string|null}} [attestation]
   * @returns {Promise<{message: string, providerSignatureId: string, outputSha256: string, signatureState: string|null, attestedBy: string|null, signatureInfo: Object}>}
   */
  async countersign(id, attestation = null) {
    return apiCall(`/proposals/${encodeURIComponent(id)}/countersign`, {
      method: 'POST',
      body: signatureAttestationBody(attestation),
    });
  },

  /**
   * Revise a sent proposal: withdraw the document already sent and open a new
   * draft carrying the same number and the next revision index.
   *
   * Irreversible. The parent becomes `superseded` and can never be sent,
   * revised, approved, signed or confirmed again, and the customer keeps the PDF
   * that was e-mailed to them — so a signature returning on it can no longer be
   * processed.
   *
   * @param {number} id - Proposal ID to revise
   * @param {string} reason - Why the transmitted offer is being withdrawn (required)
   * @returns {Promise<{id: number, proposalNumber: string, revision: number, supersedesId: number, rootId: number}>}
   */
  async revise(id, reason) {
    return apiCall(`/proposals/${encodeURIComponent(id)}/revise`, {
      method: 'POST',
      body: { reason },
    });
  },

  /**
   * Get every revision of the offer a proposal belongs to, oldest first.
   * @param {number} id - Any proposal ID in the chain
   * @returns {Promise<{rootId: number|null, revisions: Array<Object>}>}
   */
  async getRevisions(id) {
    return apiCall(`/proposals/${encodeURIComponent(id)}/revisions`);
  },

  /**
   * Manually confirm a proposal with uploaded signed PDF (admin only)
   * Bypasses normal signature verification for physically signed documents
   * @param {number} id - Proposal ID
   * @param {FormData} formData - Form data with PDF file and optional notes
   */
  async manualConfirm(id, formData) {
    return uploadFile(`/proposals/${encodeURIComponent(id)}/manual-confirm`, formData);
  },
};

/**
 * CSV Sync API
 */
export const csvApi = {
  /**
   * Trigger CSV synchronization
   */
  async sync() {
    return apiCall('/sync/csv', { method: 'POST' });
  },
};

/**
 * Dashboard Stats API
 */
export const statsApi = {
  /**
   * Get dashboard statistics
   * @returns {Promise<{customers: number, products: number, proposals: number, proposalsByStatus: Object, timestamp: string}>}
   */
  async get() {
    return apiCall('/stats');
  },
};

/** Admin marketing portfolio control. */
export const campaignsApi = {
  async getAll() {
    return apiCall('/campaigns');
  },

  async update(id, data) {
    return apiCall(`/campaigns/${encodeURIComponent(id)}`, {
      method: 'PATCH',
      body: data,
    });
  },
};

/** Recurring managed-service enrollment register. */
export const serviceEnrollmentsApi = {
  async getAll(params = {}) {
    const searchParams = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') searchParams.append(key, value);
    });
    const query = searchParams.toString();
    return apiCall(`/service-enrollments${query ? `?${query}` : ''}`);
  },

  async getRenewals() {
    return apiCall('/service-enrollments/renewals');
  },

  async create(data) {
    return apiCall('/service-enrollments', { method: 'POST', body: data });
  },

  async update(id, data) {
    return apiCall(`/service-enrollments/${encodeURIComponent(id)}`, {
      method: 'PATCH',
      body: data,
    });
  },
};

/** Admin CRM stage updates for campaign leads. */
export const leadsApi = {
  async getAll() {
    return apiCall('/leads');
  },

  async updateStatus(id, status) {
    return apiCall(`/leads/${encodeURIComponent(id)}/status`, {
      method: 'PATCH',
      body: { status },
    });
  },
};

/**
 * Sales Targets API
 */
export const salesTargetsApi = {
  /**
   * Get all sales targets for a year
   * @param {number} year - Year to filter by (defaults to current year)
   */
  async getAll(year = new Date().getFullYear()) {
    return apiCall(`/sales-targets?year=${year}`);
  },

  /**
   * Get sales targets with progress (sold quantities)
   * @param {number} year - Year to filter by (defaults to current year)
   */
  async getProgress(year = new Date().getFullYear()) {
    return apiCall(`/sales-targets/progress?year=${year}`);
  },

  /**
   * Create or update a sales target (upsert)
   * @param {Object} data - { productId, year, targetQuantity }
   */
  async upsert(data) {
    return apiCall('/sales-targets', {
      method: 'POST',
      body: data,
    });
  },

  /**
   * Bulk update sales targets
   * @param {number} year - Year for targets
   * @param {Array} targets - Array of { productId, targetQuantity }
   */
  async bulkUpdate(year, targets) {
    return apiCall('/sales-targets/bulk', {
      method: 'POST',
      body: { year, targets },
    });
  },

  /**
   * Delete a sales target
   * @param {number} id - Target ID
   */
  async delete(id) {
    return apiCall(`/sales-targets/${encodeURIComponent(id)}`, {
      method: 'DELETE',
    });
  },
};

/**
 * Product Activity Rates API
 * For managing hourly rates per product + activity type combination
 */
export const productActivityRatesApi = {
  /**
   * Get all product activity rates with optional filters
   * @param {Object} params - Query parameters (productId, activityType)
   */
  async getAll(params = {}) {
    const searchParams = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        searchParams.append(key, value);
      }
    });
    return apiCall(`/product-activity-rates?${searchParams}`);
  },

  /**
   * Get rates for a specific product
   * @param {number} productId - Product ID
   */
  async getByProduct(productId) {
    return apiCall(`/product-activity-rates/by-product/${encodeURIComponent(productId)}`);
  },

  /**
   * Create or update a product activity rate (upsert)
   * @param {Object} data - { productId, activityType, hourlyRate, description }
   */
  async upsert(data) {
    return apiCall('/product-activity-rates', {
      method: 'POST',
      body: data,
    });
  },

  /**
   * Delete a product activity rate
   * @param {number} id - Rate ID
   */
  async delete(id) {
    return apiCall(`/product-activity-rates/${encodeURIComponent(id)}`, {
      method: 'DELETE',
    });
  },

  /**
   * Get customer-specific rates
   * @param {string} customerId - Customer ID
   */
  async getCustomerRates(customerId) {
    return apiCall(`/product-activity-rates/customer/${encodeURIComponent(customerId)}`);
  },

  /**
   * Create or update a customer-specific rate
   * @param {Object} data - { customerId, productId, activityType, hourlyRate, notes }
   */
  async upsertCustomerRate(data) {
    return apiCall('/product-activity-rates/customer', {
      method: 'POST',
      body: data,
    });
  },

  /**
   * Delete a customer-specific rate
   * @param {number} id - Rate ID
   */
  async deleteCustomerRate(id) {
    return apiCall(`/product-activity-rates/customer/${encodeURIComponent(id)}`, {
      method: 'DELETE',
    });
  },

  /**
   * Lookup the applicable rate for a customer + product + activity type
   * Returns customer-specific rate if exists, otherwise base product rate
   * @param {Object} params - { customerId, productId, activityType }
   */
  async lookup(params) {
    const searchParams = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        searchParams.append(key, value);
      }
    });
    return apiCall(`/product-activity-rates/lookup?${searchParams}`);
  },

  /**
   * Get all available activity types
   */
  async getActivityTypes() {
    return apiCall('/product-activity-rates/activity-types');
  },
};

/**
 * Timesheets API
 * For tracking work hours and invoice preparation
 */
export const timesheetsApi = {
  /**
   * Get all timesheets with optional filters
   * @param {Object} params - Query parameters (startDate, endDate, customerId, billable, page, limit)
   */
  async getAll(params = {}) {
    const searchParams = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        searchParams.append(key, value);
      }
    });
    return apiCall(`/timesheets?${searchParams}`);
  },

  /**
   * Get timesheet by ID
   * @param {number} id - Timesheet ID
   */
  async getById(id) {
    return apiCall(`/timesheets/${encodeURIComponent(id)}`);
  },

  /**
   * Get summary of hours per customer for a date range
   * Useful for invoice preparation
   * @param {string} startDate - Start date (YYYY-MM-DD)
   * @param {string} endDate - End date (YYYY-MM-DD)
   */
  async getSummary(startDate, endDate) {
    return apiCall(`/timesheets/summary?startDate=${encodeURIComponent(startDate)}&endDate=${encodeURIComponent(endDate)}`);
  },

  /**
   * Get user profitability report for a year
   * Shows cost vs revenue per internal user
   * @param {number} year - Year to report on
   */
  async getUserProfitability(year) {
    return apiCall(`/timesheets/user-profitability?year=${encodeURIComponent(year)}`);
  },

  /**
   * Create a new timesheet entry
   * @param {Object} data - { date, hours, description, customerId, billable, hourlyRate }
   */
  async create(data) {
    return apiCall('/timesheets', {
      method: 'POST',
      body: data,
    });
  },

  /**
   * Update a timesheet entry
   * @param {number} id - Timesheet ID
   * @param {Object} data - Fields to update
   */
  async update(id, data) {
    return apiCall(`/timesheets/${encodeURIComponent(id)}`, {
      method: 'PUT',
      body: data,
    });
  },

  /**
   * Delete a timesheet entry
   * @param {number} id - Timesheet ID
   */
  async delete(id) {
    return apiCall(`/timesheets/${encodeURIComponent(id)}`, {
      method: 'DELETE',
    });
  },

  /**
   * Set the billed status of a timesheet entry
   * @param {number} id - Timesheet ID
   * @param {boolean} billed - New billed status
   */
  async setBilled(id, billed) {
    return apiCall(`/timesheets/${encodeURIComponent(id)}/billed`, {
      method: 'PATCH',
      body: { billed },
    });
  },

  /**
   * Get customer summary (grouped by month and activity type)
   * For customer role: returns own data; for admin: requires customerId param
   * @param {Object} params - { year, customerId }
   */
  async getCustomerSummary(params = {}) {
    const searchParams = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        searchParams.append(key, value);
      }
    });
    return apiCall(`/timesheets/customer-summary?${searchParams}`);
  },
};

/**
 * Settings API
 */
export const settingsApi = {
  /**
   * Get all settings (admin only)
   */
  async getAll() {
    return apiCall('/settings');
  },

  /**
   * Get company settings (all authenticated users)
   */
  async getCompany() {
    return apiCall('/settings/company');
  },

  /**
   * Get single setting by key
   */
  async get(key) {
    return apiCall(`/settings/${encodeURIComponent(key)}`);
  },

  /**
   * Get default VAT percentage
   */
  async getVat() {
    return apiCall('/settings/vat');
  },

  /**
   * Get proposal email template
   */
  async getEmailTemplate() {
    return apiCall('/settings/email-template/proposal');
  },

  /**
   * Send the proposal email, rendered with sample values, to the caller's own
   * address (admin only). Resolves to { sent, to } with `to` masked.
   */
  async sendTestProposalEmail() {
    return apiCall('/settings/email-template/proposal/test', {
      method: 'POST',
    });
  },

  /**
   * Update single setting (admin only)
   */
  async update(key, value) {
    return apiCall(`/settings/${encodeURIComponent(key)}`, {
      method: 'PUT',
      body: { value },
    });
  },

  /**
   * Update multiple settings at once (admin only)
   */
  async updateBulk(settings) {
    return apiCall('/settings/bulk', {
      method: 'PUT',
      body: { settings },
    });
  },

  /**
   * Get SMTP configuration (admin only)
   */
  async getSmtpConfig() {
    return apiCall('/settings/smtp/config');
  },

  /**
   * Test SMTP connection (admin only)
   */
  async testSmtp() {
    return apiCall('/settings/smtp/test', {
      method: 'POST',
    });
  },

  /**
   * Get ntfy configuration (admin only)
   */
  async getNtfyConfig() {
    return apiCall('/settings/ntfy/config');
  },

  /**
   * Test ntfy connection (admin only)
   */
  async testNtfy() {
    return apiCall('/settings/ntfy/test', {
      method: 'POST',
    });
  },
};

/**
 * Non Working Days API - holidays and company closures
 */
export const nonWorkingDaysApi = {
  /**
   * Get all non-working days, optionally filtered by year
   * @param {number} [year]
   */
  async getAll(year) {
    return apiCall(`/non-working-days${year ? `?year=${encodeURIComponent(year)}` : ''}`);
  },

  /**
   * Get the non-working days inside a date window.
   *
   * Kept separate from getAll(year) so the settings page's year filter and the
   * dashboards' horizon do not have to share one overloaded argument.
   *
   * @param {string} from - ISO YYYY-MM-DD, earliest day to return
   * @param {string} to - ISO YYYY-MM-DD, latest day to return
   */
  async getBetween(from, to) {
    const params = new URLSearchParams({ from, to });
    return apiCall(`/non-working-days?${params}`);
  },

  /**
   * Create a non-working day
   * @param {{date: string, label: string, kind?: string}} data
   */
  async create(data) {
    return apiCall('/non-working-days', { method: 'POST', body: JSON.stringify(data) });
  },

  /**
   * Delete a non-working day
   * @param {string} date - YYYY-MM-DD
   */
  async delete(date) {
    return apiCall(`/non-working-days/${encodeURIComponent(date)}`, { method: 'DELETE' });
  },
};

/**
 * User Roles API
 */
export const userRolesApi = {
  /**
   * Get current user's roles
   */
  async getMyRoles() {
    return apiCall('/user-roles/me');
  },

  /**
   * Get available roles in the system
   */
  async getAvailableRoles() {
    return apiCall('/user-roles/available');
  },

  /**
   * Get all users with their roles (admin only)
   */
  async getAllUsers() {
    return apiCall('/user-roles/users');
  },

  /**
   * Get roles for specific user (admin only)
   */
  async getUserRoles(userId) {
    return apiCall(`/user-roles/users/${encodeURIComponent(userId)}`);
  },

  /**
   * Assign role to user (admin only)
   */
  async assignRole(userId, role) {
    return apiCall(`/user-roles/users/${encodeURIComponent(userId)}/roles`, {
      method: 'POST',
      body: { role },
    });
  },

  /**
   * Remove role from user (admin only)
   */
  async removeRole(userId, role) {
    return apiCall(`/user-roles/users/${encodeURIComponent(userId)}/roles/${encodeURIComponent(role)}`, {
      method: 'DELETE',
    });
  },

  /**
   * Set all roles for user (admin only)
   */
  async setUserRoles(userId, roles) {
    return apiCall(`/user-roles/users/${encodeURIComponent(userId)}/roles`, {
      method: 'PUT',
      body: { roles },
    });
  },

  /**
   * Get internal resources with hourly costs (admin only)
   */
  async getResources() {
    return apiCall('/user-roles/resources');
  },

  /**
   * Update hourly cost and monthly hours for a resource (admin only)
   */
  async updateResourceCost(userId, hourlyCost, monthlyHours = null, notes = null) {
    return apiCall(`/user-roles/resources/${encodeURIComponent(userId)}/cost`, {
      method: 'PUT',
      body: { hourlyCost, monthlyHours, notes },
    });
  },
};

/**
 * Auth API
 */
export const authApi = {
  /**
   * Get current user info
   */
  async getUser() {
    return apiCall('/auth/user');
  },

  /**
   * Whether self-registration is open. Production answers `{ mode: 'closed' }`.
   * @returns {Promise<{mode: string}>}
   */
  async getRegistration() {
    return apiCall('/auth/registration');
  },

  /**
   * Change password for current user
   * @param {string} currentPassword - Current password
   * @param {string} newPassword - New password
   */
  async changePassword(currentPassword, newPassword) {
    return apiCall('/auth/change-password', {
      method: 'POST',
      body: { currentPassword, newPassword },
    });
  },

  /**
   * Ask for a password-reset link. The server answers `{status: 'OK'}` for any
   * address, known or not.
   * @param {string} email
   * @returns {Promise<{status: string}>}
   */
  async requestPasswordReset(email) {
    const response = await fetch(buildApiUrl('/auth/user/password/reset/token'), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', rid: 'emailpassword' },
      credentials: 'include',
      body: JSON.stringify({ formFields: [{ id: 'email', value: email }] }),
    });
    if (!response.ok) throw decorateApiError(new Error(`HTTP ${response.status}`), null, response.status);
    return readJson(response);
  },

  /**
   * Set a new password with a mailed reset token.
   * @param {string} token
   * @param {string} password
   * @returns {Promise<{httpStatus: number, status: string|undefined, revocationState: string|null, body: object}>}
   *   `revocationState` is `done` or `revoking` after a successful reset
   */
  async resetPassword(token, password) {
    const response = await fetch(buildApiUrl('/auth/user/password/reset'), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', rid: 'emailpassword' },
      credentials: 'include',
      body: JSON.stringify({ formFields: [{ id: 'password', value: password }], token }),
    });
    const body = await readJson(response);
    return {
      httpStatus: response.status,
      status: body.status,
      revocationState: response.headers.get('X-Revocation-State'),
      body,
    };
  },

  /**
   * Ask for the mail that confirms the login address by setting a new password.
   * Always answers 204.
   */
  async requestAddressConfirmation() {
    return apiCall('/auth/user/verification/request', { method: 'POST' });
  },

  /**
   * Sign out of WEBDEPLOY, OAuth grants included: this session, or every
   * session of the account with `all`.
   * @param {{all?: boolean}} [options]
   * @returns {Promise<{warnings?: string[], message?: string}>} `{}` for a 204
   */
  async terminateSessions({ all = false } = {}) {
    return apiCall('/auth/sessions/terminate', { method: 'POST', body: { all } });
  },
};

/**
 * @param {Response} response
 * @returns {Promise<object>} The parsed JSON body, or `{}`
 */
async function readJson(response) {
  try {
    const text = await response.text();
    return text ? JSON.parse(text) : {};
  } catch {
    return {};
  }
}

/**
 * Customer Portal API
 */
export const customerPortalApi = {
  /**
   * Get current customer info
   */
  async getMe() {
    return apiCall('/customer-portal/me');
  },

  /**
   * Get all proposals for the current customer
   */
  async getProposals() {
    return apiCall('/customer-portal/proposals');
  },

  /**
   * Get proposal detail
   */
  async getProposal(id) {
    return apiCall(`/customer-portal/proposals/${encodeURIComponent(id)}`);
  },

  /**
   * Get PDF URL for proposal
   */
  getPdfUrl(id) {
    return buildApiUrl(`/customer-portal/proposals/${encodeURIComponent(id)}/pdf`);
  },

  /**
   * Approve a proposal
   */
  async approveProposal(id) {
    return apiCall(`/customer-portal/proposals/${encodeURIComponent(id)}/approve`, {
      method: 'POST',
    });
  },

  /**
   * Upload a signed PDF (customer)
   * @param {number} id - Proposal ID
   * @param {FormData} formData - Form data with PDF file
   */
  async uploadSignedPdf(id, formData) {
    return uploadFile(`/customer-portal/proposals/${encodeURIComponent(id)}/upload-signed`, formData);
  },
};

/**
 * Customer Reports API
 * For admin to upload markdown reports, customers to view their reports
 */
export const customerReportsApi = {
  /**
   * Get all customer reports (admin only)
   * @param {string} [customerId] - Optional filter by customer
   */
  async list(customerId = null) {
    const params = customerId ? `?customerId=${encodeURIComponent(customerId)}` : '';
    return apiCall(`/customer-reports${params}`);
  },

  /**
   * Get own reports (customer only)
   */
  async listMy() {
    return apiCall('/customer-reports/my');
  },

  /**
   * Get single report by ID
   * @param {number} id - Report ID
   */
  async get(id) {
    return apiCall(`/customer-reports/${encodeURIComponent(id)}`);
  },

  /**
   * Upload new report (admin only)
   * @param {FormData} formData - Form data with file (.md), name, customerId
   */
  async upload(formData) {
    return uploadFile('/customer-reports', formData);
  },

  /**
   * Delete report (admin only)
   * @param {number} id - Report ID
   */
  async delete(id) {
    return apiCall(`/customer-reports/${encodeURIComponent(id)}`, {
      method: 'DELETE',
    });
  },
};

/**
 * Compliance Reports API (NIS2/CRA)
 * Reads are session-scoped: a customer sees only their own, internal/admin see all.
 */
export const complianceReportsApi = {
  /**
   * List report summaries. Customers get their own automatically; admins may
   * pass { customerId, from, to } filters.
   * @param {Object} params
   */
  async list(params = {}) {
    const searchParams = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        searchParams.append(key, value);
      }
    });
    const qs = searchParams.toString();
    return apiCall(`/compliance-reports${qs ? `?${qs}` : ''}`);
  },

  /**
   * Get one full report (envelope + structured payload under `report`).
   * @param {string} id
   */
  async get(id) {
    return apiCall(`/compliance-reports/${encodeURIComponent(id)}`);
  },
};

/**
 * Enrollment keys: what a customer buys and hands to whoever installs the
 * agent. Scoped server-side — a customer sees its own, a reseller its book's.
 */
export const enrollmentKeysApi = {
  /**
   * @param {{customerId?: string, enrollmentId?: number}} params
   */
  async list(params = {}) {
    const search = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') search.append(key, value);
    });
    const qs = search.toString();
    return apiCall(`/enrollment-keys${qs ? `?${qs}` : ''}`);
  },

  /**
   * Mint a key against a subscription. The raw key comes back exactly once.
   * @param {{serviceEnrollmentId: number, label?: string, maxHosts?: number, validUntil?: string}} body
   */
  async create(body) {
    return apiCall('/enrollment-keys', { method: 'POST', body });
  },

  /**
   * Revoke a key. Hosts it already enrolled keep working.
   * @param {string} id
   */
  async revoke(id) {
    return apiCall(`/enrollment-keys/${encodeURIComponent(id)}`, { method: 'DELETE' });
  },
};

/** The machines that took up a key. */
export const hostsApi = {
  /**
   * @param {{customerId?: string, status?: string}} params
   */
  async list(params = {}) {
    const search = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') search.append(key, value);
    });
    const qs = search.toString();
    return apiCall(`/hosts${qs ? `?${qs}` : ''}`);
  },

  /**
   * Cut one machine off and free its seat.
   * @param {string} id
   */
  async revoke(id) {
    return apiCall(`/hosts/${encodeURIComponent(id)}`, { method: 'DELETE' });
  },
};

/**
 * Partners (certified resellers).
 *
 * Everything is admin-only except `getMine()`, which is what a reseller's own
 * account calls to learn which organisation it belongs to.
 */
export const partnersApi = {
  /**
   * List resellers with customer/user counts.
   * @param {{status?: string}} params
   */
  async list(params = {}) {
    const qs = params.status ? `?status=${encodeURIComponent(params.status)}` : '';
    return apiCall(`/partners${qs}`);
  },

  /** The calling reseller's own organisation and membership. */
  async getMine() {
    return apiCall('/partners/me');
  },

  /**
   * One reseller, with its users and assigned customers.
   * @param {string} id
   */
  async get(id) {
    return apiCall(`/partners/${encodeURIComponent(id)}`);
  },

  /**
   * Create a reseller.
   * @param {Object} partner
   */
  async create(partner) {
    return apiCall('/partners', { method: 'POST', body: partner });
  },

  /**
   * Promote an existing customer to a reseller: creates the partner, gives the
   * customer's portal account a partner role and assigns the customer to its
   * own new partner.
   * @param {Object} body
   */
  async promoteCustomer(body) {
    return apiCall('/partners/from-customer', { method: 'POST', body });
  },

  /**
   * Partial update (name, contacts, status, certification dates, branding).
   * @param {string} id
   * @param {Object} changes
   */
  async update(id, changes) {
    return apiCall(`/partners/${encodeURIComponent(id)}`, { method: 'PATCH', body: changes });
  },

  /**
   * Delete a reseller. Its customers survive, unassigned.
   * @param {string} id
   */
  async remove(id) {
    return apiCall(`/partners/${encodeURIComponent(id)}`, { method: 'DELETE' });
  },

  /**
   * Attach a person to a reseller. `password` is only used when no account
   * exists for the email yet.
   * @param {string} id
   * @param {{email: string, partnerRole?: string, password?: string}} body
   */
  async addUser(id, body) {
    return apiCall(`/partners/${encodeURIComponent(id)}/users`, { method: 'POST', body });
  },

  /**
   * Detach a person and strip their partner roles.
   * @param {string} id
   * @param {string} userId
   */
  async removeUser(id, userId) {
    return apiCall(`/partners/${encodeURIComponent(id)}/users/${encodeURIComponent(userId)}`, {
      method: 'DELETE',
    });
  },

  /**
   * Assign a customer to a reseller.
   * @param {string} id
   * @param {string} customerId
   */
  async addCustomer(id, customerId) {
    return apiCall(`/partners/${encodeURIComponent(id)}/customers`, {
      method: 'POST',
      body: { customerId },
    });
  },

  /**
   * Unassign a customer.
   * @param {string} id
   * @param {string} customerId
   */
  async removeCustomer(id, customerId) {
    return apiCall(`/partners/${encodeURIComponent(id)}/customers/${encodeURIComponent(customerId)}`, {
      method: 'DELETE',
    });
  },
};

/**
 * Report Ingest Tokens API (admin only)
 */
export const reportIngestTokensApi = {
  /**
   * List tokens (no secrets).
   */
  async list() {
    return apiCall('/report-ingest-tokens');
  },

  /**
   * Mint a token. The raw token is returned once.
   * @param {string} name
   * @param {string[]|null} customerScope
   */
  async create(name, customerScope = null) {
    const body = { name };
    if (Array.isArray(customerScope) && customerScope.length > 0) {
      body.customerScope = customerScope;
    }
    return apiCall('/report-ingest-tokens', { method: 'POST', body });
  },

  /**
   * Revoke a token (soft).
   * @param {string} id
   */
  async revoke(id) {
    return apiCall(`/report-ingest-tokens/${encodeURIComponent(id)}`, {
      method: 'DELETE',
    });
  },
};

/**
 * Payroll API - Administrator Gestione Separata (admin only)
 */
export const payrollApi = {
  /**
   * Get payroll configuration
   */
  async getConfig() {
    return apiCall('/payroll/config');
  },

  /**
   * Update payroll configuration
   * @param {Object} config - Configuration values
   */
  async updateConfig(config) {
    return apiCall('/payroll/config', {
      method: 'PUT',
      body: config,
    });
  },

  /**
   * Get default payroll item definitions
   */
  async getItemDefaults() {
    return apiCall('/payroll/item-defaults');
  },

  /**
   * List payslips with optional filters
   * @param {Object} params - { year, status }
   */
  async listPayslips(params = {}) {
    const searchParams = new URLSearchParams(params);
    return apiCall(`/payroll/payslips?${searchParams}`);
  },

  /**
   * Get payslip by ID with items
   * @param {number} id
   */
  async getPayslip(id) {
    return apiCall(`/payroll/payslips/${encodeURIComponent(id)}`);
  },

  /**
   * Create a new payslip
   * @param {Object} data - { month, year, items, inpsRate, taxMode, manualTaxAmount, notes }
   */
  async createPayslip(data) {
    return apiCall('/payroll/payslips', {
      method: 'POST',
      body: data,
    });
  },

  /**
   * Update a draft payslip
   * @param {number} id
   * @param {Object} data
   */
  async updatePayslip(id, data) {
    return apiCall(`/payroll/payslips/${encodeURIComponent(id)}`, {
      method: 'PUT',
      body: data,
    });
  },

  /**
   * Confirm a draft payslip
   * @param {number} id
   */
  async confirmPayslip(id) {
    return apiCall(`/payroll/payslips/${encodeURIComponent(id)}/confirm`, {
      method: 'POST',
    });
  },

  /**
   * Delete a draft payslip
   * @param {number} id
   */
  async deletePayslip(id) {
    return apiCall(`/payroll/payslips/${encodeURIComponent(id)}`, {
      method: 'DELETE',
    });
  },

  /**
   * Download payslip PDF
   * @param {number} id
   * @returns {Promise<Blob>}
   */
  async downloadPdf(id) {
    return fetchBlob(`/payroll/payslips/${encodeURIComponent(id)}/pdf`);
  },

  /**
   * Get F24 export data
   * @param {number} id
   */
  async getF24Data(id) {
    return apiCall(`/payroll/payslips/${encodeURIComponent(id)}/f24`);
  },

  /**
   * Preview payslip calculation without saving
   * @param {Object} data - { items, inpsRate, taxMode, irpefLorda, detrazioni, ... }
   */
  async previewCalculation(data) {
    return apiCall('/payroll/calculate', {
      method: 'POST',
      body: data,
    });
  },

  /**
   * Reverse-calculate gross compensation from desired net
   * @param {Object} data - { desiredNet, inpsRate, addizRegionale, addizComunale, conguaglioIrpef }
   */
  async reverseCalculate(data) {
    return apiCall('/payroll/reverse-calculate', {
      method: 'POST',
      body: data,
    });
  },

  /**
   * Upload real payslip PDF attachment
   * @param {number} id - Payslip ID
   * @param {FormData} formData - Form data with 'pdf' field
   */
  async uploadPayslipPdf(id, formData) {
    return uploadFile(`/payroll/payslips/${encodeURIComponent(id)}/upload-pdf`, formData);
  },

  /**
   * Download uploaded payslip PDF
   * @param {number} id - Payslip ID
   * @returns {Promise<Blob>}
   */
  async downloadUploadedPdf(id) {
    return fetchBlob(`/payroll/payslips/${encodeURIComponent(id)}/uploaded-pdf`);
  },

  /**
   * Remove uploaded payslip PDF
   * @param {number} id - Payslip ID
   */
  async deleteUploadedPdf(id) {
    return apiCall(`/payroll/payslips/${encodeURIComponent(id)}/uploaded-pdf`, {
      method: 'DELETE',
    });
  },

  /**
   * Upload F24 document PDF
   * @param {number} id - Payslip ID
   * @param {FormData} formData - Form data with 'pdf' field
   */
  async uploadF24Document(id, formData) {
    return uploadFile(`/payroll/payslips/${encodeURIComponent(id)}/upload-f24`, formData);
  },

  /**
   * Download uploaded F24 document
   * @param {number} id - Payslip ID
   * @returns {Promise<Blob>}
   */
  async downloadF24Document(id) {
    return fetchBlob(`/payroll/payslips/${encodeURIComponent(id)}/f24-document`);
  },

  /**
   * Remove uploaded F24 document
   * @param {number} id - Payslip ID
   */
  async deleteF24Document(id) {
    return apiCall(`/payroll/payslips/${encodeURIComponent(id)}/f24-document`, {
      method: 'DELETE',
    });
  },

};

/**
 * An error from the OAuth consent endpoints.
 *
 * The consent screen has to tell apart half a dozen refusals that all arrive as
 * a failed fetch — an expired session, someone else's challenge, a scope this
 * server will never grant — and each one deserves different words and a
 * different way out. A plain `Error` carrying only a message throws that away,
 * so the status and the parsed body travel with the error.
 */
export class OAuthApiError extends Error {
  /**
   * @param {string} message Human-readable message
   * @param {{status?: number, code?: string, payload?: Object}} [details]
   */
  constructor(message, details = {}) {
    super(message);
    this.name = 'OAuthApiError';
    /** @type {number} HTTP status, or 0 when the request never completed */
    this.status = details.status ?? 0;
    /** @type {string} Machine-readable error code from the backend */
    this.code = details.code || 'request_failed';
    /** @type {Object} Full parsed response body, when there was one */
    this.payload = details.payload || {};
  }
}

/**
 * Call a root-mounted OAuth endpoint, preserving the status and body on failure.
 *
 * Not routed through `apiCall`: that helper prefixes `/api` and flattens every
 * failure into a message-only `Error`.
 *
 * @param {string} path Root-relative path
 * @param {{method?: string, body?: Object}} [options]
 * @returns {Promise<Object>} Parsed JSON body
 * @throws {OAuthApiError}
 */
async function oauthCall(path, options = {}) {
  const { method = 'GET', body } = options;

  let response;
  try {
    response = await fetch(buildRootUrl(path), {
      method,
      credentials: 'include',
      headers: body ? { 'Content-Type': 'application/json' } : {},
      body: body ? JSON.stringify(body) : undefined,
    });
  } catch (networkError) {
    // A dead API host and a dead Hydra are the same thing to the user: the
    // authorization service cannot be reached right now.
    throw new OAuthApiError(networkError.message || 'Network error', {
      status: 0,
      code: 'hydra_unavailable',
    });
  }

  const rawText = await response.text();
  /** @type {Object} */
  let payload = {};
  let payloadIsObject = false;
  try {
    const parsed = rawText ? JSON.parse(rawText) : null;
    if (parsed && typeof parsed === 'object') {
      payload = parsed;
      payloadIsObject = true;
    }
  } catch (parseError) {
    // SuperTokens' session middleware answers 401 with the bare string
    // "unauthorised", so on a *failure* a non-JSON body is expected rather than
    // exceptional. On a success it is not — see below.
  }

  if (!response.ok) {
    throw new OAuthApiError(
      payload.message || payload.error_description || payload.error || `HTTP ${response.status}`,
      {
        status: response.status,
        code: payload.error || (response.status === 401 ? 'unauthorised' : 'request_failed'),
        payload,
      }
    );
  }

  // A 2xx that is not a JSON object never came from the consent endpoints: both
  // answer success with one, always. It is something else on the path — the dev
  // server handing back index.html for an unproxied /oauth request, a reverse
  // proxy's own page, a captive portal. Treating it as an empty body would let
  // the consent screen render a card with no client and no scopes: a blank
  // screen with an Approve button on it. Say so instead.
  if (!payloadIsObject) {
    throw new OAuthApiError(`Non-JSON response from ${path} (HTTP ${response.status})`, {
      status: response.status,
      code: 'invalid_response',
    });
  }

  return payload;
}

/**
 * OAuth consent API (root-mounted, session-credentialed).
 *
 * Both calls send cookies: the backend takes the subject from the SuperTokens
 * session and never from the challenge, so an uncredentialed call is a 401 by
 * design rather than an anonymous consent.
 */
export const oauthApi = {
  /**
   * What the consent screen renders: client, requested/grantable/denied scopes.
   * @param {string} consentChallenge Challenge issued by Hydra
   * @returns {Promise<{client: {id: string, name: string}, requestedScopes: string[], grantableScopes: string[], deniedScopes: string[], audience: string[]}>}
   */
  async consentDetails(consentChallenge) {
    const query = new URLSearchParams({ consent_challenge: consentChallenge }).toString();
    return oauthCall(`/oauth/consent/details?${query}`);
  },

  /**
   * Record the decision and get the URL to hand back to Hydra.
   * @param {string} consentChallenge Challenge issued by Hydra
   * @param {boolean} approve True to grant, false to refuse
   * @returns {Promise<{redirectTo: string}>}
   */
  async submitConsent(consentChallenge, approve) {
    return oauthCall('/oauth/consent', {
      method: 'POST',
      body: { consentChallenge, approve: approve === true },
    });
  },
};

/**
 * Qualified signature API (firma qualificata) — admin only.
 *
 * A visible signature is placed over two round trips: upload the document to
 * open a job, then sign the job. The document stays on the server between the
 * two so the bytes that were previewed are the bytes that get signed.
 */
export const signaturesApi = {
  /**
   * Whether signing is configured on this instance, and its limits.
   * @returns {Promise<{enabled: boolean, missing: string[], sandbox: boolean, maxUploadBytes: number, previewDpi: number, defaultSignerName: string|null}>}
   */
  async status() {
    return apiCall('/signatures/status');
  },

  /**
   * Upload a PDF and open a signing job.
   * @param {File} file PDF to sign
   * @returns {Promise<{id: string, originalFilename: string, pageCount: number, pages: Array<{page: number, width: number, height: number}>, previewDpi: number, inputSha256: string, expiresAt: string}>}
   */
  async createJob(file) {
    const formData = new FormData();
    formData.append('pdf', file);
    return uploadFile('/signatures/jobs', formData);
  },

  /**
   * URL of a rendered page image. Used as an <img> src, so it must be a URL
   * rather than a fetched blob — the browser sends the session cookie with it.
   * @param {string} jobId Job identifier
   * @param {number} page 1-based page number
   * @returns {string}
   */
  pageImageUrl(jobId, page) {
    return buildApiUrl(`/signatures/jobs/${encodeURIComponent(jobId)}/pages/${page}`);
  },

  /**
   * Sign the job's document.
   *
   * `rect` is in preview-image pixels with the origin at the top-left; the
   * backend converts it to PDF points. Omitted entirely for an invisible
   * signature.
   *
   * @param {string} jobId Job identifier
   * @param {{page: number, imageVisible: boolean, rect?: object, reason?: string, location?: string, signerName?: string}} options Placement
   * @returns {Promise<{id: string, status: string, outputFilename: string, outputSha256: string, providerSignatureId: string}>}
   */
  async sign(jobId, options) {
    return apiCall(`/signatures/jobs/${encodeURIComponent(jobId)}/sign`, {
      method: 'POST',
      body: options,
    });
  },

  /**
   * Download the signed PDF.
   * @param {string} jobId Job identifier
   * @returns {Promise<Blob>}
   */
  async download(jobId) {
    return fetchBlob(`/signatures/jobs/${encodeURIComponent(jobId)}/download`);
  },

  /**
   * CAdES-sign an arbitrary file, returning the `.p7m` as a blob.
   * @param {File} file File to sign
   * @returns {Promise<Blob>}
   */
  async signCades(file) {
    const formData = new FormData();
    formData.append('file', file);
    const response = await fetch(buildApiUrl('/signatures/cades'), {
      method: 'POST',
      credentials: 'include',
      body: formData,
    });
    if (!response.ok) {
      let errorData;
      try {
        errorData = JSON.parse(await response.text());
      } catch (parseError) {
        errorData = { error: 'Firma non riuscita' };
      }
      throw new Error(errorData.error || `HTTP ${response.status}`);
    }
    return response.blob();
  },

  /**
   * Read the signature audit trail.
   * @param {Object} params Filters: source, ok, since, limit
   * @returns {Promise<{entries: Object[]}>}
   */
  async audit(params = {}) {
    const searchParams = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        searchParams.append(key, value);
      }
    });
    const qs = searchParams.toString();
    return apiCall(`/signatures/audit${qs ? `?${qs}` : ''}`);
  },
};

/**
 * Generic API helper for components
 * Provides direct access to raw API methods
 */
export const api = {
  get: (endpoint) => apiCall(endpoint),
  post: (endpoint, data) => apiCall(endpoint, { method: 'POST', body: data }),
  put: (endpoint, data) => apiCall(endpoint, { method: 'PUT', body: data }),
  delete: (endpoint) => apiCall(endpoint, { method: 'DELETE' }),
  uploadFile,
  getBlob: fetchBlob,
};
